import { Fragment, useMemo } from 'react';
import { IconButton, Tooltip, useEventCallback } from '@mui/material';
import { createSearchParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { ActionProps } from 'shell/view-models/actions-props';
import { Visibility as VisibilityIcon } from '@mui/icons-material';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import {
  releaseIdCardResponseTransformerForSearch,
  ReleaseIdCardTransofrmSearchType,
} from 'front-office/utils/release-id-card-response-transformer';
import { HttpResponse } from 'shell/models/http-response';
import { NoteAnomalie, ResponseAnprData } from 'shell/models/person';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { AnomalyType } from 'front-office/constants/anomaly';

export const releaseCardAnomalyLockCheck = (noteAnomalie?: Array<NoteAnomalie>): boolean => {
  return !!noteAnomalie?.some(
    (item) =>
      item.codiceAnomalia === AnomalyType.Generic ||
      item.codiceAnomalia === AnomalyType.Type50 ||
      item.codiceAnomalia === AnomalyType.Type82 ||
      item.codiceAnomalia === AnomalyType.Type83 ||
      item.codiceAnomalia === AnomalyType.Type73
  );
};

const ActionsList = ({ cell, filterPersonForm }: ActionProps): React.ReactElement => {
  const navigate = useNavigate();

  const { userProfile } = useUserProfile();

  const getAnprData = useHttpClient<
    HttpResponse<ResponseAnprData>,
    ReleaseIdCardTransofrmSearchType
  >(`${__PERSON_NEW_API_URL__}/persona/ricerca-rilascio-cie`, {
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
      data: {},
    },
    transformer: (response) => releaseIdCardResponseTransformerForSearch(response, userProfile),
  });

  const onVisibilityPress = useEventCallback(() => {
    return new Promise((resolve) => {
      (
        getAnprData({
          data: {
            criteriRicerca: {
              idSchedaSoggettoANPR: cell?.anpr,
              codiceFiscale: cell?.fiscalCode || undefined,
            },
            datiRichiesta: {
              ricercaNazionale:
                filterPersonForm?.subjectData?.isNationalResearch ||
                filterPersonForm?.commonSearch?.isNationalResearch
                  ? '1'
                  : '0',
            },
          },
        }) as ResponsePromise<HttpResponse<ResponseAnprData>, ReleaseIdCardTransofrmSearchType>
      ).then(() => {
        navigate({
          pathname: '/sportello/rilascio-carta/rilascio',
          search: `?${createSearchParams({
            codiceFiscale: cell?.fiscalCode || '',
            idSchedaSoggettoANPR: cell?.anpr || '',
            ricercaNazionale:
              filterPersonForm?.subjectData?.isNationalResearch?.toString() ||
              filterPersonForm?.commonSearch?.isNationalResearch?.toString() ||
              'false',
          })}`,
        });
        resolve(cell);
      });
    });
  });

  const hasError = useMemo(() => {
    return !!releaseCardAnomalyLockCheck(cell?.dati3002?.risposta3002OK?.noteAnomalie);
  }, [cell?.dati3002?.risposta3002OK?.noteAnomalie]);

  return (
    <Fragment>
      <Tooltip arrow title={hasError ? "Rilascio carta d'identità bloccato" : ''}>
        <span>
          <IconButton
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onVisibilityPress();
            }}
            disabled={hasError}
            aria-label="Visualizza elemento"
            aria-haspopup="true"
            style={{
              padding: '4px',
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </span>
      </Tooltip>
    </Fragment>
  );
};

ActionsList.displayName = 'Action list release id card component';

export default ActionsList;
